import React, { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { useAgent } from "../../Context/AgentContext";

const AgentDashboard = ({ setUser, setIsToken }) => {
  const navigate = useNavigate();
  const {
    agent,
    listings,
    stats,
    loading,
    error,
    refreshListings,
    clearError,
    logoutAgent
  } = useAgent();

  const [recentListings, setRecentListings] = useState([]);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);

  // Auto-refresh on component mount - just reload the page once if needed
  useEffect(() => {
    // Optional: You can add logic here if you want to auto-refresh on mount
    // For now, we'll let the natural page load handle the data fetching
  }, []);

  useEffect(() => {
    if (listings && listings.length > 0) {
      const sortedListings = listings
        .sort((a, b) => new Date(b.createdAt || b.created_at || b.dateAdded || 0) - new Date(a.createdAt || a.created_at || a.dateAdded || 0))
        .slice(0, 6);
      setRecentListings(sortedListings);
    } else {
      setRecentListings([]);
    }
  }, [listings]);

  const handleLogout = () => {
    // Use the context logout method (this handles the main logout)
    logoutAgent();
    
    // Update parent component state only if the functions exist
    if (typeof setUser === 'function') {
      setUser(null);
    }
    if (typeof setIsToken === 'function') {
      setIsToken(false);
    }
    
    // Navigate to home
    navigate('/');
  };

  const formatPrice = (price) => {
    if (!price) return 'Contact for Price';
    const numericPrice = price.toString().replace(/[^\d]/g, '');
    return `¥${parseInt(numericPrice).toLocaleString()}`;
  };

  const getStatusColor = (listing) => {
    if (listing.status === 'approved' || (!listing.underCheck && listing.status !== 'pending_approval')) {
      return 'approved-status';
    } else if (listing.status === 'rejected') {
      return 'rejected-status';
    }
    return 'pending-status';
  };

  const getStatusText = (listing) => {
    if (listing.status === 'approved' || (!listing.underCheck && listing.status !== 'pending_approval')) {
      return 'Approved';
    } else if (listing.status === 'rejected') {
      return 'Rejected';
    }
    return 'Pending Review';
  };

  // Simple page refresh function
  const handleRefresh = () => {
    window.location.reload();
  };

  const handleNavigate = (path) => {
    navigate(path);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-grey)' }}>
        <div className="flex flex-col items-center space-y-4">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center animate-pulse"
            style={{ background: 'linear-gradient(to bottom right, var(--medium-sea-green), var(--sea-green))' }}>
            <i className="fas fa-building text-white text-2xl"></i>
          </div>
          <p className="light-header-nospacing">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-grey)' }}>
      {/* FontAwesome CDN */}
      <link
        rel="stylesheet"
        href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css"
      />

      {/* Custom Styles */}
      <style>{`
        .approved-status {
          color: var(--sea-green);
          background-color: rgba(14, 177, 169, 0.1);
          border: 1px solid rgba(14, 177, 169, 0.2);
        }
        .pending-status {
          color: var(--dark-grey);
          background-color: rgba(148, 153, 149, 0.1);
          border: 1px solid rgba(148, 153, 149, 0.2);
        }
        .rejected-status {
          color: #dc2626;
          background-color: rgba(220, 38, 38, 0.1);
          border: 1px solid rgba(220, 38, 38, 0.2);
        }
        .teal-border {
          border-color: rgba(14, 177, 169, 0.2);
        }
        .teal-bg {
          background-color: rgba(14, 177, 169, 0.05);
        }
        .teal-hover:hover {
          background-color: rgba(14, 177, 169, 0.1);
        }
        .gradient-primary {
          background: linear-gradient(to right, var(--medium-sea-green), var(--sea-green));
        }
        .gradient-primary:hover {
          background: linear-gradient(to right, var(--sea-green), #0da399);
        }
        .text-primary {
          color: var(--medium-sea-green);
        }
        .text-secondary {
          color: var(--dark-grey);
        }
        .border-primary {
          border-color: rgba(16, 201, 192, 0.2);
        }
        .refresh-spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Header */}
      <header className="bg-[--medium-sea-green] shadow-sm border-b teal-border">
        <div className="f-section mx-auto" style={{ padding: '0 clamp(6px, 4vw, 30px)' }}>
          {/* Top Header Bar */}
          <div className="h-20 flex items-center justify-between">
            <div className="flex items-center">
              <div className="h-6 border-l" style={{ borderColor: 'var(--medium-sea-green)' }}></div>
              <h2
                className="m-0 font-bold text-white" // Fixed font weight
                style={{
                  fontSize: 'clamp(12px, 2.5vmin, 24px)', // Responds to both width and height
                  paddingTop: 'clamp(0.25rem, 1vmin, 0.5rem)'
                }}
              >
                Agent Dashboard
              </h2>
              {error && (
                <div className="flex items-center space-x-2">
                  <i className="fas fa-exclamation-triangle text-white opacity-70"></i>
                  <button
                    onClick={handleRefresh}
                    className="text-sm text-white hover:opacity-70 underline non-selectable"
                  >
                    Refresh Page
                  </button>
                </div>
              )}
            </div>

            {/* Agent Profile Dropdown */}

          </div>

          {/* Welcome Section */}
          <div className="pb-8">
            <div className="flex items-center justify-between">
              {/* <h2 
                className="m-0 font-bold text-white"
                style={{
                  fontSize: 'clamp(24px, 6vw, 48px)',
                  lineHeight: 'clamp(28px, 7vw, 52px)',
                  paddingTop: 'clamp(0.25rem, 1vmin, 0.5rem)',
                  paddingBottom: 'clamp(0.25rem, 1vmin, 0.5rem)'
                }}
              >
                Welcome back, {agent?.name || 'Agent'}!
              </h2> */}


              <h1
                className="m-0 font-bold text-white"
                style={{
                  fontSize: 'clamp(24px, 8vmin, 72px)', // Responds to both width and height
                  lineHeight: 'clamp(28px, 9vmin, 78px)', // Proportional line height
                  paddingTop: 'clamp(0.25rem, 1vmin, 0.5rem)',
                  paddingBottom: 'clamp(0.25rem, 1vmin, 0.5rem)'
                }}
              >
                Welcome back, {agent?.name || 'Agent'}!
              </h1>
              <button
                onClick={handleRefresh}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-xl transition-colors non-selectable"
              >
                <i className="fas fa-sync-alt mr-2"></i>
                Refresh
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Error Display */}
      {error && (
        <div className="f-section mx-auto py-4" style={{ padding: '16px clamp(6px, 4vw, 30px)' }}>
          <div className="teal-bg border border-primary px-4 py-3 rounded-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <i className="fas fa-exclamation-triangle mr-2 text-primary"></i>
                <span className="light-header-nospacing" style={{ margin: 0 }}>{error}</span>
              </div>
              <button
                onClick={clearError}
                className="hover:opacity-70 transition-opacity non-selectable"
              >
                <i className="fas fa-times text-primary"></i>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="f-section mx-auto py-8" style={{ padding: '32px clamp(6px, 4vw, 30px)' }}>
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Listings */}
          <div className="bg-white p-6 rounded-xl shadow-sm border-primary border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-secondary">Total Listings</p>
                <p className="text-2xl font-bold" style={{ color: 'var(--color-2)' }}>
                  {stats?.total || 0}
                </p>
                <p className="text-xs text-secondary mt-1">All your properties</p>
              </div>
              <div className="w-12 h-12 teal-bg rounded-lg flex items-center justify-center">
                <i className="fas fa-building text-primary text-xl"></i>
              </div>
            </div>
          </div>

          {/* Approved Listings */}
          <div className="bg-white p-6 rounded-xl shadow-sm border-primary border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-secondary">Approved</p>
                <p className="text-2xl font-bold text-primary">{stats?.approved || 0}</p>
                <p className="text-xs text-secondary mt-1">Live on platform</p>
              </div>
              <div className="w-12 h-12 teal-bg rounded-lg flex items-center justify-center">
                <i className="fas fa-check-circle text-primary text-xl"></i>
              </div>
            </div>
          </div>

          {/* Pending Approval */}
          <div className="bg-white p-6 rounded-xl shadow-sm border-primary border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-secondary">Pending</p>
                <p className="text-2xl font-bold text-secondary">{stats?.pending || 0}</p>
                <p className="text-xs text-secondary mt-1">Under review</p>
              </div>
              <div className="w-12 h-12 teal-bg rounded-lg flex items-center justify-center">
                <i className="fas fa-clock text-primary text-xl"></i>
              </div>
            </div>
          </div>

          {/* Rejected Listings */}
          <div className="bg-white p-6 rounded-xl shadow-sm border-primary border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-secondary">Rejected</p>
                <p className="text-2xl font-bold" style={{ color: '#dc2626' }}>
                  {stats?.rejected || 0}
                </p>
                <p className="text-xs text-secondary mt-1">Needs attention</p>
              </div>
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(220, 38, 38, 0.1)' }}>
                <i className="fas fa-times-circle text-xl" style={{ color: '#dc2626' }}></i>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-sm border-primary border p-6 mb-8">
          <h3 className="dark-header-nospacing mb-4" style={{ color: 'var(--color-2)' }}>
            Quick Actions
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => handleNavigate('/agent/add-listing')}
              className="flex items-center p-4 text-white rounded-lg bg-[--medium-sea-green] hover:bg-[--sea-green] transition-all duration-300 group non-selectable"
            >
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mr-4">
                <i className="fas fa-plus text-white text-xl"></i>
              </div>
              <div className="text-left">
                <p className="font-medium">Add New Listing</p>
                <p className="text-sm text-white/80">List a new property</p>
              </div>
              <i className="fas fa-arrow-right ml-auto opacity-0 group-hover:opacity-100 transition-opacity"></i>
            </button>

            <button
              onClick={() => handleNavigate('/agent/my-listings')}
              className="flex items-center p-4 teal-bg rounded-lg teal-hover transition-colors group non-selectable"
              style={{ color: 'var(--color-2)' }}
            >
              <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center mr-4 shadow-sm">
                <i className="fas fa-list text-primary text-xl"></i>
              </div>
              <div className="text-left">
                <p className="font-medium">Manage Listings</p>
                <p className="text-sm text-secondary">Edit your properties ({stats?.total || 0})</p>
              </div>
              <i className="fas fa-arrow-right ml-auto opacity-0 group-hover:opacity-100 transition-opacity"></i>
            </button>

            <button
              onClick={() => handleNavigate('/agent/profile')}
              className="flex items-center p-4 teal-bg rounded-lg teal-hover transition-colors group non-selectable"
              style={{ color: 'var(--color-2)' }}
            >
              <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center mr-4 shadow-sm">
                <i className="fas fa-user-edit text-primary text-xl"></i>
              </div>
              <div className="text-left">
                <p className="font-medium">Profile Settings</p>
                <p className="text-sm text-secondary">
                  Update your info
                  {!agent?.is_verified && ' • Get verified'}
                </p>
              </div>
              <i className="fas fa-arrow-right ml-auto opacity-0 group-hover:opacity-100 transition-opacity"></i>
            </button>
          </div>
        </div>

        {/* Recent Listings */}
        <div className="bg-white rounded-xl shadow-sm border-primary border p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="dark-header-nospacing" style={{ color: 'var(--color-2)', margin: 0 }}>
              Recent Listings
            </h3>
            <button
              onClick={() => handleNavigate('/agent/my-listings')}
              className="text-primary hover:opacity-70 transition-colors text-sm font-medium non-selectable"
            >
              View All ({stats?.total || 0}) →
            </button>
          </div>

          {recentListings.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {recentListings.map((listing) => (
                <div key={listing.property_id} className="group cursor-pointer">
                  <div className="teal-bg rounded-lg h-48 mb-4 overflow-hidden relative">
                    {listing.Images && listing.Images.length > 0 ? (
                      <img
                        src={listing.Images[0]}
                        alt={listing.Address}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="w-full h-full flex items-center justify-center">
                      <i className="fas fa-building text-primary text-4xl opacity-50"></i>
                    </div>

                    {/* Status Badge */}
                    <div className={`absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(listing)}`}>
                      {getStatusText(listing)}
                    </div>

                    {/* Featured Badge */}
                    {listing.featured && (
                      <div className="absolute top-3 left-3 gradient-primary text-white px-2 py-1 rounded-full text-xs font-medium">
                        <i className="fas fa-star mr-1"></i>
                        Featured
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-medium group-hover:text-primary transition-colors line-clamp-2" style={{ color: 'var(--color-2)' }}>
                      {listing.Address}
                    </h4>
                    <p className="text-sm text-secondary">
                      {listing.Prefecture} • {listing.Municipality || listing.municipality}
                    </p>
                    <div className="flex items-center justify-between">
                      <p className="text-lg font-semibold text-primary">{formatPrice(listing.Price)}</p>
                      <div className="flex items-center space-x-2 text-xs text-secondary">
                        {listing.Year_of_Construction && (
                          <span>{listing.Year_of_Construction}</span>
                        )}
                        {listing.Room_Type && (
                          <span>• {listing.Room_Type}</span>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-secondary">
                      ID: {listing.property_id}
                      {listing.createdAt && (
                        <span> • Created: {new Date(listing.createdAt).toLocaleDateString()}</span>
                      )}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 teal-bg rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-home text-primary text-2xl opacity-50"></i>
              </div>
              <p className="light-header-nospacing mb-4">No listings yet</p>
              <p className="text-sm text-secondary mb-4">
                Start building your property portfolio by adding your first listing.
              </p>
              <button
                onClick={() => handleNavigate('/agent/add-listing')}
                className="inline-flex items-center px-6 py-3 gradient-primary text-white rounded-lg hover:opacity-90 transition-colors non-selectable"
              >
                <i className="fas fa-plus mr-2"></i>
                Add Your First Listing
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AgentDashboard;